// ===================================================
// ФАЙЛ: frontend/src/hooks/usePermissions.js
// ОБНОВЛЕНО: Использует новый useAuth из Redux вместо AuthContext
// ===================================================
import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  hasPermission as hasPermissionByRole,
  isAdmin as isAdminByRole,
  isSuperAdmin,
  isOwner,
  getRolePermissions,
  USER_ROLES,
  PERMISSIONS,
} from '../utils/constants';
import { connectionPermissionsAllow } from '../utils/permissionConnection';
import {
  hasServerEffectivePermission,
} from '../utils/rolePermissionsFlatten';

/**
 * Стабильный пустой объект для случаев, когда у пользователя нет
 * `connection_permissions` (основной контекст компании). Хранится модулем,
 * чтобы потребители получали ту же ссылку между рендерами.
 */
const EMPTY_CONNECTION_PERMISSIONS = Object.freeze({});

/**
 * Хук для работы с правами пользователя
 * Обновлен для работы с Redux authSlice
 */
// Нормализация роли из бэкенда к кодам фронтенда
const normalizeUserRole = (rawRole) => {
  if (!rawRole) return null;
  const role = String(rawRole).toLowerCase();
  
  // ✅ Уже код роли из БД
  if ([USER_ROLES.SUPER_ADMIN, USER_ROLES.PLATFORM_ADMIN, USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.OPERATOR, USER_ROLES.VIEWER, USER_ROLES.USER].includes(role)) {
    return role;
  }
  
  // ✅ Отображаемые имена (ru)
  if (['супер администратор', 'суперадминистратор'].includes(role)) return USER_ROLES.SUPER_ADMIN;
  if (['администратор платформы', 'platform admin'].includes(role)) return USER_ROLES.PLATFORM_ADMIN;
  if (['владелец'].includes(role)) return USER_ROLES.OWNER;
  if (['администратор'].includes(role)) return USER_ROLES.ADMIN;
  if (['администратор компании'].includes(role)) return USER_ROLES.ADMIN;
  if (['владелец компании'].includes(role)) return USER_ROLES.OWNER;
  if (['менеджер'].includes(role)) return USER_ROLES.MANAGER;
  if (['оператор'].includes(role)) return USER_ROLES.OPERATOR;
  if (['просмотр', 'наблюдатель'].includes(role)) return USER_ROLES.VIEWER;
  if (['пользователь'].includes(role)) return USER_ROLES.USER;
  
  // ✅ Коды из БД (на всякий случай)
  if (role === 'super_admin') return USER_ROLES.SUPER_ADMIN;
  if (role === 'platform_admin') return USER_ROLES.PLATFORM_ADMIN;
  if (role === 'owner') return USER_ROLES.OWNER;
  if (role === 'company_owner') return USER_ROLES.OWNER;
  if (role === 'admin') return USER_ROLES.ADMIN;
  if (role === 'company_admin') return USER_ROLES.ADMIN;
  if (role === 'manager') return USER_ROLES.MANAGER;
  if (role === 'operator') return USER_ROLES.OPERATOR;
  if (role === 'viewer') return USER_ROLES.VIEWER;
  if (role === 'user') return USER_ROLES.USER;
  
  // ⚠️ Неизвестная роль - минимальные права
  console.warn('Неизвестная роль пользователя:', rawRole);
  return USER_ROLES.USER;
};

export const usePermissions = () => {
  const { user, company, userRole: userRoleFromState, contextReady } = useAuth();
  const hasCompanyContext = Boolean(contextReady && user?.id && company?.id);
  const isMembershipCompanyContext = hasCompanyContext && user?.is_membership_company === true;
  const isPrimaryCompanyContext = hasCompanyContext && user?.is_primary_company !== false;
  const isDirectCompanyContext = isPrimaryCompanyContext || isMembershipCompanyContext;
  const rawRole = userRoleFromState || user?.role || user?.roleName || user?.role_name || user?.role_code;
  const legacyRole = normalizeUserRole(rawRole);
  const platformFlagKnown = typeof user?.is_platform_admin === 'boolean';
  const platformAuthority = Boolean(contextReady && user?.id && (
    platformFlagKnown ? user.is_platform_admin === true : (!isMembershipCompanyContext && isSuperAdmin(legacyRole))
  ));
  const platformRole = normalizeUserRole(user?.platform_role || rawRole);
  const scopedRole = normalizeUserRole(
    Object.prototype.hasOwnProperty.call(user || {}, 'company_role')
      ? user.company_role
      : (isMembershipCompanyContext ? user?.membership_role : rawRole)
  );
  // An explicit server platform flag cannot be overridden by a membership/legacy role string.
  const effectiveRole = platformAuthority
    ? (isSuperAdmin(platformRole) ? platformRole : USER_ROLES.PLATFORM_ADMIN)
    : (isSuperAdmin(scopedRole) ? null : scopedRole);

  // [] is an authoritative denial, distinct from an older response without this field.
  const serverEffectivePermissions = useMemo(() => (
    Array.isArray(user?.effective_permissions)
      ? user.effective_permissions.filter((permission) => typeof permission === 'string')
      : null
  ), [user?.effective_permissions]);

  const connectionPermissionsKey = useMemo(() => {
    const src = (user?.connection_permissions && typeof user.connection_permissions === 'object'
      ? user.connection_permissions : null)
      || (company?.connection_permissions && typeof company.connection_permissions === 'object'
        ? company.connection_permissions : null);
    if (!src) return '';
    try { return JSON.stringify(src); } catch (_e) { return ''; }
  }, [user?.connection_permissions, company?.connection_permissions]);

  const connectionPermissions = useMemo(() => {
    if (!connectionPermissionsKey) return EMPTY_CONNECTION_PERMISSIONS;
    try { return JSON.parse(connectionPermissionsKey); } catch (_e) { return EMPTY_CONNECTION_PERMISSIONS; }
  }, [connectionPermissionsKey]);

  const hasConnectionPermission = useCallback((resource, action = 'read') => {
    if (!hasCompanyContext || isDirectCompanyContext || platformAuthority) return false;
    if (connectionPermissions?.all === true) return true;
    const normalizedResource = String(resource || '').trim().toLowerCase();
    const normalizedAction = String(action || 'read').trim().toLowerCase();
    if (!normalizedResource) return false;
    return connectionPermissions?.[normalizedResource]?.[normalizedAction] === true;
  }, [hasCompanyContext, isDirectCompanyContext, platformAuthority, connectionPermissions]);

  const useRolePermissions = hasCompanyContext && (isDirectCompanyContext || platformAuthority);
  const hasPermission = useCallback((permission) => {
    if (!hasCompanyContext || !permission) return false;
    if (!platformAuthority && [PERMISSIONS.ADMIN_ACCESS, PERMISSIONS.SYSTEM_ADMIN].includes(permission)) return false;
    if (useRolePermissions) {
      if (serverEffectivePermissions !== null) {
        return hasServerEffectivePermission(serverEffectivePermissions, permission);
      }
      // Personal membership never inherits a primary role when its scoped result is missing.
      return !isMembershipCompanyContext && hasPermissionByRole(effectiveRole, permission);
    }
    return connectionPermissionsAllow(permission, connectionPermissions);
  }, [hasCompanyContext, platformAuthority, useRolePermissions, serverEffectivePermissions,
    isMembershipCompanyContext, effectiveRole, connectionPermissions]);

  const hasPermissions = useCallback((permissions, requireAll = true) => {
    if (!Array.isArray(permissions) || permissions.length === 0) return false;
    return requireAll ? permissions.every(hasPermission) : permissions.some(hasPermission);
  }, [hasPermission]);

  const hasScopedRoleAuthority = useRolePermissions && (
    serverEffectivePermissions !== null ? serverEffectivePermissions.length > 0 : !isMembershipCompanyContext
  );
  const isAdminFn = useCallback(() => hasScopedRoleAuthority && isAdminByRole(effectiveRole),
    [hasScopedRoleAuthority, effectiveRole]);
  const isSuperAdminFn = useCallback(() => platformAuthority, [platformAuthority]);
  const isOwnerFn = useCallback(() => hasScopedRoleAuthority && isOwner(effectiveRole),
    [hasScopedRoleAuthority, effectiveRole]);

  const getAllPermissions = useCallback(() => {
    if (!useRolePermissions) return [];
    if (serverEffectivePermissions !== null) {
      const permissions = serverEffectivePermissions.includes('*')
        ? getRolePermissions(effectiveRole) : serverEffectivePermissions;
      return platformAuthority ? permissions : permissions.filter((permission) => (
        permission !== PERMISSIONS.ADMIN_ACCESS && permission !== PERMISSIONS.SYSTEM_ADMIN
      ));
    }
    return isMembershipCompanyContext ? [] : getRolePermissions(effectiveRole);
  }, [useRolePermissions, serverEffectivePermissions, effectiveRole, platformAuthority, isMembershipCompanyContext]);

  const canManageSensitiveCompanySettings = useCallback(() => {
    if (!hasCompanyContext) return false;
    return useRolePermissions ? hasPermission(PERMISSIONS.SETTINGS_UPDATE) : hasConnectionPermission('settings', 'update');
  }, [hasCompanyContext, useRolePermissions, hasPermission, hasConnectionPermission]);

  const canManageCompanyBilling = useCallback(() => {
    if (!hasCompanyContext) return false;
    if (useRolePermissions) {
      return hasPermission('billing.update') || hasPermission(PERMISSIONS.SETTINGS_UPDATE)
        || (serverEffectivePermissions === null && isOwnerFn());
    }
    return hasConnectionPermission('billing', 'update') || hasConnectionPermission('settings', 'update');
  }, [hasCompanyContext, useRolePermissions, hasPermission, serverEffectivePermissions, isOwnerFn, hasConnectionPermission]);

  return useMemo(() => ({
    hasPermission, hasPermissions,
    isAdmin: isAdminFn,
    isSuperAdmin: isSuperAdminFn,
    isOwner: isOwnerFn,
    getAllPermissions,
    user,
    userRole: effectiveRole,
    company,
    isPrimaryCompanyContext,
    isMembershipCompanyContext,
    isDirectCompanyContext,
    connectionPermissions,
    hasConnectionPermission,
    canManageSensitiveCompanySettings,
    canManageCompanyBilling,
    isActive: user?.is_active === true,
  }), [hasPermission, hasPermissions, isAdminFn, isSuperAdminFn, isOwnerFn, getAllPermissions,
    user, effectiveRole, company, isPrimaryCompanyContext, isMembershipCompanyContext,
    isDirectCompanyContext, connectionPermissions, hasConnectionPermission,
    canManageSensitiveCompanySettings, canManageCompanyBilling]);
};
